/**
 * Swym Wishlist Integration - Cart Removal Enhancement
 * 
 * This script automatically removes items from the cart when they are added to the wishlist.
 * It ONLY listens to Swym's addedToWishlist event - does NOT intercept clicks.
 * 
 * No impact on other functionality - completely non-intrusive.
 */

(function() {
  'use strict';

  // Debug mode - set to false in production
  const DEBUG = true; // Temporarily enabled to debug line number issues
  const debugLog = DEBUG ? console.log.bind(console) : () => {};

  // Track which variant IDs are currently being processed to prevent duplicate removals
  const processingVariants = new Set();
  
  // Store line numbers for variant IDs (captured from DOM when button is clicked)
  const variantLineMap = new Map();
  
  // Store the most recently clicked line number (most reliable - use this directly)
  let lastClickedLineNumber = null;
  let lastClickedTime = 0;
  let lastClickedVariantId = null;
  
  // Track recent wishlist button clicks to prevent redirects
  let recentWishlistClickTime = 0;
  
  /**
   * Gets the line number from the DOM for a wishlist button
   * @param {HTMLElement} button - The wishlist button element
   * @returns {number|null} The line number (1-based) or null if not found
   */
  function getLineNumberFromDOM(button) {
    debugLog('[getLineNumber] Starting line number detection for button:', button);
    
    // Find the cart row containing this button
    const cartRow = button.closest('tr[role="row"], .cart-items__table-row');
    if (!cartRow) {
      debugLog('[getLineNumber] No cart row found');
      return null;
    }
    debugLog('[getLineNumber] Found cart row:', cartRow);
    
    // Method 1: Try to find remove button with on:click attribute
    const removeButton = cartRow.querySelector('button[on\\:click], button[onclick]');
    if (removeButton) {
      const onClickAttr = removeButton.getAttribute('on:click') || removeButton.getAttribute('onclick');
      debugLog('[getLineNumber] Found remove button with onClick:', onClickAttr);
      if (onClickAttr) {
        const match = onClickAttr.match(/onLineItemRemove\/(\d+)/);
        if (match && match[1]) {
          const line = parseInt(match[1], 10);
          if (!isNaN(line) && line > 0) {
            debugLog('[getLineNumber] Method 1 SUCCESS: Found line', line, 'from remove button');
            return line;
          }
        }
      }
    } else {
      debugLog('[getLineNumber] Method 1: No remove button found');
    }
    
    // Method 2: Try to find quantity selector with data-line-index
    const quantitySelector = cartRow.querySelector('quantity-selector-component, [data-line-index]');
    if (quantitySelector) {
      const lineIndex = quantitySelector.getAttribute('data-line-index');
      debugLog('[getLineNumber] Found quantity selector with data-line-index:', lineIndex);
      if (lineIndex !== null && lineIndex !== '') {
        const line = parseInt(lineIndex, 10) + 1; // line-index is 0-based
        if (!isNaN(line) && line > 0) {
          debugLog('[getLineNumber] Method 2 SUCCESS: Found line', line, 'from quantity selector');
          return line;
        }
      }
    } else {
      debugLog('[getLineNumber] Method 2: No quantity selector found');
    }
    
    // Method 3: Count row position in tbody
    const tbody = cartRow.closest('tbody');
    if (tbody) {
      const itemRows = tbody.querySelectorAll('tr[role="row"], .cart-items__table-row');
      const rowIndex = Array.from(itemRows).indexOf(cartRow);
      debugLog('[getLineNumber] Found tbody with', itemRows.length, 'rows, clicked row index:', rowIndex);
      if (rowIndex !== -1) {
        const line = rowIndex + 1; // Convert to 1-based
        debugLog('[getLineNumber] Method 3 SUCCESS: Found line', line, 'from row index');
        return line;
      }
    } else {
      debugLog('[getLineNumber] Method 3: No tbody found');
    }
    
    debugLog('[getLineNumber] FAILED: Could not determine line number');
    return null;
  }
  
  /**
   * Captures line number when wishlist button is clicked
   */
  function captureLineNumberOnClick(event) {
    if (!event.isTrusted) return;
    
    const wishlistButton = event.target.closest('.swym-add-to-wishlist-view-product, button[data-swaction="addToWishlist"]');
    if (wishlistButton) {
      // Capture line number from DOM (most reliable method)
      const lineNumber = getLineNumberFromDOM(wishlistButton);
      if (lineNumber) {
        const variantId = wishlistButton.getAttribute('data-variant-id');
        if (variantId) {
          const variantIdStr = String(variantId);
          // Store by variant ID
          variantLineMap.set(variantIdStr, lineNumber);
          // Store the most recent click with timestamp (most reliable - use this directly)
          lastClickedLineNumber = lineNumber;
          lastClickedVariantId = variantIdStr;
          lastClickedTime = Date.now();
          debugLog('[capture] Captured line', lineNumber, 'for variant', variantId, '(stored as most recent at', lastClickedTime, ')');
        }
      }
      
      // Mark event to prevent redirect - this runs in capture phase BEFORE dialog.js
      Object.defineProperty(event, '_swymWishlistClick', {
        value: true,
        writable: false,
        configurable: false
      });
      
      // Track recent wishlist click time for redirect prevention
      recentWishlistClickTime = Date.now();
    }
  }
  
  /**
   * Prevents redirects by intercepting window.location.href assignments
   */
  function preventWishlistRedirects() {
    // Intercept window.location.href assignments
    const originalLocation = window.location;
    let locationProxy = new Proxy(originalLocation, {
      set: function(target, prop, value) {
        if (prop === 'href' || prop === 'pathname') {
          // Check if this is a cart redirect and we have a recent wishlist click
          const valueStr = String(value);
          if (valueStr.includes('/cart')) {
            const timeSinceClick = Date.now() - recentWishlistClickTime;
            if (timeSinceClick < 1000) { // Within 1 second of wishlist click
              debugLog('[location-patch] Blocked cart redirect for wishlist button (clicked', timeSinceClick, 'ms ago)');
              return true; // Prevent the redirect
            }
          }
        }
        // For all other cases, allow the assignment
        target[prop] = value;
        return true;
      },
      get: function(target, prop) {
        return target[prop];
      }
    });
    
    // Try to replace window.location (may not work in all browsers due to security)
    try {
      Object.defineProperty(window, 'location', {
        value: locationProxy,
        writable: false,
        configurable: false
      });
    } catch (e) {
      // If we can't override location, we'll rely on the dialog patch instead
      debugLog('[location-patch] Could not override window.location, relying on dialog patch');
    }
  }

  /**
   * Patches dialog.js showDialog to prevent redirects for wishlist button clicks
   * This MUST run early to prevent redirects before Swym can load
   */
  function patchDialogShowDialog() {
    // Wait for dialog component to be defined
    if (typeof customElements === 'undefined') {
      // If customElements not ready, wait for it
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', patchDialogShowDialog);
      } else {
        setTimeout(patchDialogShowDialog, 100);
      }
      return;
    }
    
    customElements.whenDefined('cart-drawer-component').then(() => {
      const CartDrawerComponent = customElements.get('cart-drawer-component');
      if (!CartDrawerComponent) {
        // Retry if not defined yet
        setTimeout(patchDialogShowDialog, 100);
        return;
      }
      
      // Get the prototype
      const proto = CartDrawerComponent.prototype;
      if (!proto || !proto.showDialog) {
        setTimeout(patchDialogShowDialog, 100);
        return;
      }
      
      // Check if already patched
      if (proto.showDialog._swymPatched) {
        return;
      }
      
      // Store original showDialog
      const originalShowDialog = proto.showDialog;
      
      // Override showDialog
      proto.showDialog = function(event) {
        // Only block redirects for actual user clicks (MouseEvent) on wishlist buttons
        // Programmatic events (like CartAddEvent) should pass through normally
        if (event && event.isTrusted && event.target && typeof event.target.closest === 'function') {
          const wishlistButton = event.target.closest('.swym-add-to-wishlist-view-product, .swymcs-add-to-wishlist-button, button[data-swaction="addToWishlist"]');
          if (wishlistButton) {
            debugLog('[dialog-patch] Blocked redirect for wishlist button click - allowing Swym to load');
            // Don't call original showDialog - just return to prevent redirect
            // This allows Swym to load and handle the wishlist add
            return;
          }
        }
        
        // For all other events (including programmatic CartAddEvent), call original method
        return originalShowDialog.call(this, event);
      };
      
      // Mark as patched
      proto.showDialog._swymPatched = true;
      
      debugLog('[dialog-patch] Successfully patched cart-drawer-component showDialog method');
    }).catch(() => {
      // If customElements.whenDefined fails, retry
      setTimeout(patchDialogShowDialog, 100);
    });
  }

  /**
   * Gets all cart section IDs that need to be updated
   * @returns {Set<string>} Set of section IDs
   */
  function getCartSectionIds() {
    const sectionsToUpdate = new Set();
    
    // Get from cart-items-component elements
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    
    // Fallback: try to get from cart page or drawer
    if (sectionsToUpdate.size === 0) {
      const cartPageSection = document.querySelector('[data-section-id]');
      if (cartPageSection instanceof HTMLElement && cartPageSection.dataset.sectionId) {
        sectionsToUpdate.add(cartPageSection.dataset.sectionId);
      }
      
      const cartDrawer = document.querySelector('cart-drawer-component[data-section-id]');
      if (cartDrawer instanceof HTMLElement && cartDrawer.dataset.sectionId) {
        sectionsToUpdate.add(cartDrawer.dataset.sectionId);
      }
    }
    
    return sectionsToUpdate;
  }

  /**
   * Dispatches cart update events in the format expected by the theme
   * @param {Object} parsedResponse - The cart API response
   * @param {string} source - Source identifier for debugging
   */
  function dispatchCartUpdateEvents(parsedResponse, source) {
    const sections = parsedResponse.sections || {};
    const itemCount = parsedResponse.item_count || 0;

    // Dispatch standard cart:update event
    const updateEvent = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: parsedResponse,
        sourceId: 'wishlist-remove',
        data: {
          sections,
          itemCount,
          source: 'wishlist-add',
        },
        sections, // Also include at top level for compatibility
      }
    });
    document.dispatchEvent(updateEvent);

    // Store cart state in sessionStorage for cart page refresh
    try {
      sessionStorage.setItem('cart_last_updated', Date.now().toString());
      sessionStorage.setItem('cart_item_count', itemCount.toString());
    } catch (storageError) {
      debugLog(`[${source}] Error storing cart state:`, storageError);
    }

    // Reinitialize Swym buttons if drawer is open
    setTimeout(() => {
      if (typeof _swat !== 'undefined' && _swat.initializeActionButtons) {
        const isCartDrawerOpen = document.querySelector('cart-drawer-component[auto-open]');
        if (isCartDrawerOpen) {
          _swat.initializeActionButtons("body");
        }
      }
    }, 100);
  }

  /**
   * Removes item from cart by line number
   * @param {number} line - The cart line number (1-based)
   * @param {string} source - Source identifier for debugging
   * @returns {Promise<boolean>}
   */
  async function removeItemFromCartByLine(line, source = 'wishlist') {
    try {
      // Validate line number
      const lineNumber = typeof line === 'string' ? parseInt(line, 10) : Math.floor(Number(line));
      if (!lineNumber || lineNumber < 1 || !Number.isInteger(lineNumber)) {
        debugLog(`[${source}] Invalid line number:`, line);
        return false;
      }

      // Verify cart has items and line exists
      const cartCheckResponse = await fetch('/cart.js');
      if (cartCheckResponse.ok) {
        const cartData = await cartCheckResponse.json();
        if (!cartData.items || cartData.items.length === 0) {
          debugLog(`[${source}] Cart is empty`);
          return false;
        }
        if (lineNumber > cartData.items.length) {
          debugLog(`[${source}] Line ${lineNumber} exceeds cart items (${cartData.items.length})`);
          return false;
        }
      }

      const sectionsToUpdate = getCartSectionIds();
      const cartChangeUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_change_url) 
        ? Theme.routes.cart_change_url 
        : '/cart/change.js';

      const removeFromCartBody = JSON.stringify({
        line: lineNumber,
        quantity: 0,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const cartChangeResponse = await fetch(cartChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: removeFromCartBody,
      });

      if (!cartChangeResponse.ok) {
        debugLog(`[${source}] Cart change failed:`, cartChangeResponse.status);
        return false;
      }

      const responseText = await cartChangeResponse.text();
      const parsedResponse = JSON.parse(responseText);

      dispatchCartUpdateEvents(parsedResponse, source);
      return true;

    } catch (error) {
      debugLog(`[${source}] Error removing item from cart:`, error);
      return false;
    }
  }

  /**
   * Swym callback function - handles Swym events
   * This is the ONLY way we interact - through Swym's official event system
   */
  function onSwymLoadCallback(swat) {
    // Update button states when wishlist changes
    swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, e => {
      const productId = e.detail.d.empi;
      document.querySelectorAll(`.swymcs-wishlist-button button[data-empi="${productId}"]`).forEach(btn => {
        if (btn.parentElement && typeof btn.parentElement.updateState === 'function') {
          btn.parentElement.updateState();
        }
      });
    });

    // Handle items added to wishlist - remove from cart
    // This event ONLY fires AFTER Swym successfully adds to wishlist
    swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, async e => {
      const productId = e.detail.d.empi;
      const variantId = e.detail.d.epi;
      
      // Update button states
      document.querySelectorAll(`.swymcs-wishlist-button button[data-empi="${productId}"]`).forEach(btn => {
        if (btn.parentElement && typeof btn.parentElement.updateState === 'function') {
          btn.parentElement.updateState();
        }
      });

      if (!variantId) return;

      const variantIdStr = String(variantId);
      
      // Skip if already being processed
      if (processingVariants.has(variantIdStr)) {
        debugLog('[swym-event] Variant', variantId, 'already being processed');
        return;
      }

      processingVariants.add(variantIdStr);
      debugLog('[swym-event] Item successfully added to wishlist, variant ID:', variantId);

      // Priority 1: ALWAYS use the most recently clicked line number if available (within 5 seconds)
      // This is the MOST RELIABLE method - we know exactly which line was clicked
      let line = null;
      const timeSinceClick = Date.now() - lastClickedTime;
      
      if (lastClickedLineNumber && timeSinceClick < 5000) {
        // Verify the line still exists in cart (don't check variant - just verify line exists)
        try {
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            const cartData = await cartResponse.json();
            if (cartData.items && cartData.items.length >= lastClickedLineNumber) {
              // Line exists - use it! This is the line that was actually clicked
              line = lastClickedLineNumber;
              debugLog('[swym-event] Using most recently clicked line number:', line, '(clicked', timeSinceClick, 'ms ago - line verified to exist in cart)');
            } else {
              debugLog('[swym-event] Recent click line', lastClickedLineNumber, 'no longer exists in cart (cart has', cartData.items?.length || 0, 'items)');
            }
          }
        } catch (error) {
          debugLog('[swym-event] Error verifying clicked line, using it anyway:', error);
          // If we can't verify, still use it if very recent (< 1 second)
          if (timeSinceClick < 1000) {
            line = lastClickedLineNumber;
            debugLog('[swym-event] Using most recently clicked line number:', line, '(clicked', timeSinceClick, 'ms ago - very recent, using despite verification error)');
          }
        }
      }
      
      // Priority 2: Try to get from our captured map (by variant ID)
      if (!line) {
        line = variantLineMap.get(variantIdStr) || variantLineMap.get(String(variantId));
        if (line) {
          debugLog('[swym-event] Using captured line number from map:', line, 'for variant', variantId);
        }
      }
      
      // Priority 3: Fallback - Find the line number from cart by variant ID
      // WARNING: This will find the FIRST matching variant, which may be wrong if there are duplicates
      // Only use this as a last resort
      if (!line) {
        try {
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            const cartData = await cartResponse.json();
            const variantIdNum = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
            
            if (cartData.items && cartData.items.length > 0) {
              const cartItemIndex = cartData.items.findIndex(item => {
                const itemId = typeof item.id === 'string' ? parseInt(item.id, 10) : item.id;
                return itemId === variantIdNum;
              });

              if (cartItemIndex !== -1) {
                line = cartItemIndex + 1;
                debugLog('[swym-event] Found item in cart at line:', line, 'for variant', variantId, '(from API fallback - WARNING: may be wrong if duplicates exist)');
              } else {
                debugLog('[swym-event] Item not found in cart for variant', variantId);
              }
            } else {
              debugLog('[swym-event] Cart is empty');
            }
          }
        } catch (error) {
          debugLog('[swym-event] Error checking cart:', error);
        }
      }

      // Remove from cart if we found the line
      if (line) {
        // Small delay to ensure wishlist is fully processed
        setTimeout(() => {
          removeItemFromCartByLine(line, 'swym-event').finally(() => {
            processingVariants.delete(variantIdStr);
            // Clean up the map entry and recent click tracking
            variantLineMap.delete(variantIdStr);
            variantLineMap.delete(String(variantId));
            // Clear recent click if this was the one we used
            if (lastClickedLineNumber === line) {
              lastClickedLineNumber = null;
              lastClickedVariantId = null;
              lastClickedTime = 0;
            }
          });
        }, 300);
      } else {
        debugLog('[swym-event] Could not determine line number for variant', variantId);
        processingVariants.delete(variantIdStr);
      }
    });

    // Handle variant changes
    swat.evtLayer.addEventListener(swat.JSEvents.variantChanged, e => {
      const productId = e.detail.d.product.id;
      const variantId = e.detail.d.variant.id;
      if (productId && variantId) {
        document.querySelectorAll(`.swymcs-wishlist-button button[data-empi="${productId}"]`).forEach(btn => {
          btn.dataset.epi = variantId;
          if (btn.parentElement && typeof btn.parentElement.updateState === 'function') {
            btn.parentElement.updateState();
          }
        });
      }
    });
  }

  /**
   * Handles cart page navigation - refreshes if cart was recently updated
   */
  function handleCartPageNavigation() {
    if (window.location.pathname.includes('/cart') || window.location.pathname === '/cart') {
      try {
        const lastUpdated = sessionStorage.getItem('cart_last_updated');
        if (lastUpdated) {
          const timeSinceUpdate = Date.now() - parseInt(lastUpdated, 10);
          // If cart was updated in the last 5 seconds, trigger a refresh
          if (timeSinceUpdate < 5000) {
            const updateEvent = new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                source: 'page-navigation',
                data: {
                  sections: {},
                  itemCount: parseInt(sessionStorage.getItem('cart_item_count') || '0', 10),
                },
              }
            });
            document.dispatchEvent(updateEvent);

            // Re-render cart sections
            document.querySelectorAll('cart-items-component[data-section-id]').forEach(component => {
              if (component instanceof HTMLElement && component.dataset.sectionId) {
                if (typeof sectionRenderer !== 'undefined' && sectionRenderer.renderSection) {
                  sectionRenderer.renderSection(component.dataset.sectionId, { cache: false });
                }
              }
            });
          }
        }
      } catch (error) {
        debugLog('[cart-page] Error checking cart update:', error);
      }
    }
  }

  /**
   * Reinitializes Swym buttons when cart is updated
   * This ensures Swym event listeners are attached to newly added cart items
   */
  function reinitializeSwymButtons() {
    // Wait a bit for DOM to update, then initialize Swym buttons
    setTimeout(() => {
      if (typeof _swat !== 'undefined' && _swat.initializeActionButtons) {
        debugLog('[swym-init] Reinitializing Swym buttons after cart update');
        _swat.initializeActionButtons("body");
      } else {
        // If Swym not loaded yet, try again
        setTimeout(reinitializeSwymButtons, 200);
      }
    }, 100);
  }

  /**
   * Listens for cart updates and reinitializes Swym buttons
   */
  function setupCartUpdateListener() {
    // Listen for cart:update events (fired when items are added/removed)
    document.addEventListener('cart:update', function(event) {
      debugLog('[cart-update] Cart updated, reinitializing Swym buttons');
      reinitializeSwymButtons();
    }, { passive: true });
    
    // Also listen for when cart drawer opens
    const observer = new MutationObserver(function(mutations) {
      const cartDrawer = document.querySelector('cart-drawer-component[auto-open], cart-drawer-component dialog[open]');
      if (cartDrawer) {
        debugLog('[cart-drawer] Cart drawer opened, reinitializing Swym buttons');
        reinitializeSwymButtons();
      }
    });
    
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['auto-open']
      });
    }
  }

  // Register Swym callback - this is the ONLY initialization we do
if (!window.SwymCallbacks) {
  window.SwymCallbacks = [];
}
  window.SwymCallbacks.push(onSwymLoadCallback);
  
  // Setup cart update listener to reinitialize Swym buttons
  setupCartUpdateListener();

  // CRITICAL: Patch dialog.js IMMEDIATELY to prevent redirects before Swym loads
  // This must run as early as possible
  patchDialogShowDialog();
  
  // Also try again after DOM is ready in case customElements wasn't ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchDialogShowDialog);
  }
  
  // Also patch any existing instances immediately
  function patchExistingInstances() {
    document.querySelectorAll('cart-drawer-component').forEach(instance => {
      if (instance && instance.showDialog && !instance.showDialog._swymPatched) {
        const original = instance.showDialog;
        instance.showDialog = function(event) {
          if (event && event.isTrusted && event.target && typeof event.target.closest === 'function') {
            const wishlistButton = event.target.closest('.swym-add-to-wishlist-view-product, button[data-swaction="addToWishlist"]');
            if (wishlistButton) {
              debugLog('[instance-patch] Blocked redirect for wishlist button click');
              return;
            }
          }
          return original.call(this, event);
        };
        instance.showDialog._swymPatched = true;
      }
    });
  }
  
  // Patch existing instances immediately and watch for new ones
  patchExistingInstances();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchExistingInstances);
  }
  
  // Watch for new cart drawer instances
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(() => {
      patchExistingInstances();
    });
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }
  
  // Prevent redirects by patching window.location
  preventWishlistRedirects();
  
  // Capture line numbers when wishlist buttons are clicked (in capture phase - runs FIRST)
  document.addEventListener('click', captureLineNumberOnClick, true);

  // Handle cart page navigation
  window.addEventListener('pageshow', handleCartPageNavigation);

})();